import type { BrokerCapabilities, BrokerCsvImportCapabilities } from "@/services/brokerIntegrationApi"
import type {
  DemoCatalogBroker,
  DemoCatalogFeGuidance,
  DemoParseContract,
} from "@/types/demoEntryAnalysis"
import { isCtraderBroker } from "@/utils/ctraderBroker"
import { isCustomCsvBrokerName } from "@/utils/customCsvAccountDisplay"
import {
  futuresOrdersPlatformDisplayName,
  isFuturesOrdersCsvBroker,
} from "@/utils/futuresOrdersCsvBroker"
import { isHyperliquidBroker } from "@/utils/hyperliquidBroker"
import { isMt4Broker } from "@/utils/mt4Broker"
import { isMt5Broker } from "@/utils/mt5Broker"
import { isTradingViewConnectBrandSlug, findTradingViewLaneCapability } from "@/utils/tradingViewConnectBrand"
import { isTradingViewBroker, TRADINGVIEW_BROKER_SLUG } from "@/utils/tradingViewBroker"
import {
  isTradingViewPaperBroker,
  isTradingViewPaperHiddenCsvField,
  TRADINGVIEW_PAPER_BROKER_SLUG,
} from "@/utils/tradingViewPaperBroker"

/** Custom CSV mapping / inspect fields — handled by dedicated demo UI, not generic inputs. */
export const DEMO_PARSE_STRUCTURED_FIELDS = new Set([
  "column_mapping",
  "value_transforms",
  "defaults",
  "allow_partial",
  "header_row_index",
  "import_mode",
  "layout_shape_id",
])

const DEFAULT_SKIP_FIELDS = new Set([
  "broker_name",
  "executing_broker_name",
  "account_name",
  "account_code",
  "display_name",
  "traded_asset_classes",
  "preview_token",
  "save_template",
  "save_profile",
  "template_slug",
  "profile_slug",
  "cash_history_file",
  "lot_to_units",
])

function isImportBlock(
  value: unknown
): value is BrokerCsvImportCapabilities {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value)
}

function hasFileUploadSupport(broker: DemoCatalogBroker): boolean {
  if (broker.custom_csv || isCustomCsvBrokerName(broker.broker_name)) return true
  if (broker.demo_parse) return true
  if (broker.file_upload === false) return false
  if (isImportBlock(broker.csv_import) || broker.csv_import === true) return true
  if (isImportBlock(broker.xlsx_import) || broker.xlsx_import === true) return true
  if (isImportBlock(broker.html_import) || broker.html_import === true) return true
  return Boolean(broker.file_upload)
}

/** Canonical Form key for a catalog field name (aliases → demo cohort keys). */
export function demoParseFormKey(
  field: string,
  parse?: DemoParseContract | null,
  guidance?: DemoCatalogFeGuidance | null
): string {
  const fromParse = parse?.form_field_map?.[field]
  if (fromParse) return fromParse
  const fromGuidance = guidance?.aliases?.[field]
  if (fromGuidance) return fromGuidance
  if (field === "statement_time_zone") return "time_zone"
  if (field === "instrument") return "import_instrument"
  return field
}

export function isDemoTimezoneField(field: string): boolean {
  const lower = field.toLowerCase()
  return lower === "time_zone" || lower === "statement_time_zone" || lower.includes("timezone")
}

export function resolveDemoCatalogBroker(
  brokers: DemoCatalogBroker[],
  brokerName: string
): DemoCatalogBroker | null {
  const needle = brokerName.trim().toLowerCase()
  const exact =
    brokers.find((b) => b.broker_name.trim().toLowerCase() === needle) || null
  if (exact) return exact
  // Paper lane may share Backtest demo_parse until BE lists tradingview_paper.
  if (isTradingViewConnectBrandSlug(brokerName)) {
    return (
      brokers.find(
        (b) => b.broker_name.trim().toLowerCase() === TRADINGVIEW_BROKER_SLUG
      ) || null
    )
  }
  return null
}

export function resolveDemoParse(
  brokers: DemoCatalogBroker[],
  brokerName: string
): DemoParseContract | null {
  return resolveDemoCatalogBroker(brokers, brokerName)?.demo_parse ?? null
}

function normalizeDemoSkipKey(value: string): string {
  return value.trim().toLowerCase().replace(/[\s-]+/g, "_")
}

/** Account chrome and other fields demo must never collect or POST. */
export function isDemoSkippedParseField(fieldOrFormKey: string): boolean {
  const key = normalizeDemoSkipKey(fieldOrFormKey)
  if (!key) return true
  if (DEFAULT_SKIP_FIELDS.has(key) || DEMO_PARSE_STRUCTURED_FIELDS.has(key)) return true
  // CamelCase / label variants (accountName, Account Name).
  if (key === "accountname" || key === "accountcode" || key === "displayname") return true
  return false
}

/** Fields to render as simple inputs (timezone, instrument, utc offset, …). */
export function listDemoParseSimpleFields(
  parse: DemoParseContract | null | undefined,
  guidance?: DemoCatalogFeGuidance | null,
  brokerName?: string | null
): Array<{ field: string; required: boolean; formKey: string; label: string }> {
  if (!parse) return []
  const skip = new Set<string>([
    ...Array.from(DEFAULT_SKIP_FIELDS),
    ...(guidance?.skip_fields || []).map(normalizeDemoSkipKey),
    ...Array.from(DEMO_PARSE_STRUCTURED_FIELDS),
  ])

  const out: Array<{ field: string; required: boolean; formKey: string; label: string }> = []
  const seen = new Set<string>()

  const push = (field: string, required: boolean) => {
    if (!field) return
    const fieldKey = normalizeDemoSkipKey(field)
    if (skip.has(fieldKey) || isDemoSkippedParseField(field) || seen.has(fieldKey)) return
    const formKey = demoParseFormKey(field, parse, guidance)
    if (skip.has(normalizeDemoSkipKey(formKey)) || isDemoSkippedParseField(formKey)) return
    if (
      isTradingViewPaperBroker(brokerName) &&
      (isTradingViewPaperHiddenCsvField(field) || isTradingViewPaperHiddenCsvField(formKey))
    ) {
      return
    }
    seen.add(fieldKey)
    const label =
      parse.field_labels?.[field] ||
      parse.field_labels?.[formKey] ||
      field.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    out.push({ field, required, formKey, label })
  }

  for (const field of parse.required_fields || []) push(field, true)
  for (const field of parse.optional_fields || []) push(field, false)
  return out
}

export function demoParseAcceptAttr(parse: DemoParseContract | null | undefined): string {
  const types = parse?.accepted_file_types
  if (!types?.length) return ".csv,.xlsx,.xls,.htm,.html"
  return types.join(",")
}

export type DemoFileFormatKind = "csv" | "xlsx" | "html" | "mixed"

/** Human-facing file format from catalog `accepted_file_types` / `import_type`. */
export function resolveDemoFileFormat(
  parse: DemoParseContract | null | undefined
): {
  kind: DemoFileFormatKind
  /** Short badge: CSV, Excel (XLSX), HTML */
  shortLabel: string
  /** Field label: "CSV file", "Excel (.xlsx) file", … */
  fieldLabel: string
  /** One-line helper under the picker. */
  fieldDescription: string
  extensions: string[]
} {
  const raw = (parse?.accepted_file_types || []).map((t) => t.trim().toLowerCase()).filter(Boolean)
  const fromImportType = String(parse?.import_type || "").toLowerCase()

  const extensions =
    raw.length > 0
      ? raw
      : fromImportType === "xlsx_import"
        ? [".xlsx"]
        : fromImportType === "html_import"
          ? [".htm", ".html"]
          : [".csv"]

  const hasCsv = extensions.some((e) => e.includes("csv"))
  const hasXlsx = extensions.some((e) => e.includes("xls"))
  const hasHtml = extensions.some((e) => e.includes("htm"))

  const kinds: DemoFileFormatKind[] = []
  if (hasCsv) kinds.push("csv")
  if (hasXlsx) kinds.push("xlsx")
  if (hasHtml) kinds.push("html")
  const kind: DemoFileFormatKind =
    kinds.length === 1 ? kinds[0] : kinds.length === 0 ? "csv" : "mixed"

  // Title already names the format (CSV / Excel / HTML); keep the helper generic.
  const fieldDescription = "Use the export format described above for your broker."

  if (kind === "xlsx") {
    return {
      kind,
      shortLabel: "Excel (XLSX)",
      fieldLabel: "Excel (.xlsx) file",
      fieldDescription,
      extensions,
    }
  }
  if (kind === "html") {
    return {
      kind,
      shortLabel: "HTML",
      fieldLabel: "HTML report file",
      fieldDescription,
      extensions,
    }
  }
  if (kind === "mixed") {
    return {
      kind,
      shortLabel: "Statement file",
      fieldLabel: "Trade file",
      fieldDescription,
      extensions,
    }
  }
  return {
    kind: "csv",
    shortLabel: "CSV",
    fieldLabel: "CSV file",
    fieldDescription,
    extensions,
  }
}


function toCapability(broker: DemoCatalogBroker): BrokerCapabilities {
  const isCustom =
    Boolean(broker.custom_csv) || isCustomCsvBrokerName(broker.broker_name)
  const importType = String(broker.import_type || "").trim() || "csv_import"
  const blockFromType = broker[importType]
  const csvBlock = isImportBlock(broker.csv_import)
    ? broker.csv_import
    : importType === "csv_import" && isImportBlock(blockFromType)
      ? blockFromType
      : undefined
  const xlsxBlock = isImportBlock(broker.xlsx_import)
    ? broker.xlsx_import
    : importType === "xlsx_import" && isImportBlock(blockFromType)
      ? blockFromType
      : undefined
  const htmlBlock = isImportBlock(broker.html_import)
    ? broker.html_import
    : importType === "html_import" && isImportBlock(blockFromType)
      ? blockFromType
      : undefined

  const fallbackSupported: BrokerCsvImportCapabilities | undefined = broker.demo_parse
    ? {
        supported: true,
        accepted_file_types: broker.demo_parse.accepted_file_types,
        notes: broker.demo_parse.notes,
      }
    : { supported: true }

  const tradeSync =
    broker.trade_sync && typeof broker.trade_sync === "object"
      ? broker.trade_sync
      : undefined

  return {
    broker_name: broker.broker_name,
    display_name: broker.display_name || broker.broker_name,
    auth: {
      supports_auto_sync: false,
      required_fields: [],
      optional_fields: [],
    },
    analytics: {},
    csv_import:
      csvBlock ||
      (importType === "csv_import" || (!xlsxBlock && !htmlBlock) || isCustom
        ? fallbackSupported
        : undefined),
    xlsx_import: xlsxBlock || (importType === "xlsx_import" ? fallbackSupported : undefined),
    html_import: htmlBlock || (importType === "html_import" ? fallbackSupported : undefined),
    ...(tradeSync ? { trade_sync: tradeSync } : {}),
  } as BrokerCapabilities
}

/**
 * Map catalog v2 rows into connect-wizard capability shape for BrokerChoiceCard.
 * Demo always exposes both TradingView lanes (Paper + Backtest) for the path picker.
 */
export function ensureDemoTradingViewLaneCapabilities(
  brokers: BrokerCapabilities[]
): BrokerCapabilities[] {
  const backtest = findTradingViewLaneCapability(brokers, TRADINGVIEW_BROKER_SLUG)
  const paper = findTradingViewLaneCapability(brokers, TRADINGVIEW_PAPER_BROKER_SLUG)
  if (backtest && paper) return brokers

  const template = backtest ?? paper
  if (!template) return brokers

  const out = [...brokers]
  if (backtest && !paper) {
    out.push({
      ...backtest,
      broker_name: TRADINGVIEW_PAPER_BROKER_SLUG,
      display_name: "TradingView Paper Trading",
    })
  } else if (paper && !backtest) {
    out.push({
      ...paper,
      broker_name: TRADINGVIEW_BROKER_SLUG,
      display_name: "TradingView",
    })
  }
  return out
}

export function demoCatalogToBrokerCapabilities(
  brokers: DemoCatalogBroker[]
): BrokerCapabilities[] {
  const caps = brokers.filter(hasFileUploadSupport).map(toCapability)
  return ensureDemoTradingViewLaneCapabilities(caps)
}

export function countTradingViewCatalogLanes(brokers: DemoCatalogBroker[]): number {
  return brokers.filter((b) => isTradingViewConnectBrandSlug(b.broker_name)).length
}

/**
 * Upload-step shell subtitle — same short CSV blurbs as Broker Management, not BE
 * `demo_parse.notes` (those are internal demo field hints).
 */
export function resolveDemoUploadSubtitle(brokerName: string): string {
  const slug = String(brokerName || "").trim()
  if (!slug) {
    return "CSV import: upload your broker statement with the required fields below."
  }
  if (isCustomCsvBrokerName(slug)) {
    return "Upload your CSV and map columns to import trades into Tessera."
  }
  if (isTradingViewBroker(slug)) {
    return "Import a TradingView Strategy Tester trade list CSV."
  }
  if (isTradingViewPaperBroker(slug)) {
    return "Import a TradingView Paper Trading Order History CSV."
  }
  if (isMt5Broker(slug)) return "Import an MT5 Trade History Report (.xlsx)."
  if (isMt4Broker(slug)) return "Import an MT4 Detailed Report (.htm/.html)."
  if (isHyperliquidBroker(slug)) return "Import a Hyperliquid trade history CSV export."
  if (isCtraderBroker(slug)) return "Import a cTrader Account or Statement CSV export."
  if (isFuturesOrdersCsvBroker(slug)) {
    return `Import a ${futuresOrdersPlatformDisplayName(slug)} Orders CSV export.`
  }
  if (slug.toLowerCase() === "ibkr") {
    return "CSV import: upload your IBKR Flex statement."
  }
  return "CSV import: upload your broker statement with the required fields below."
}
